import validator from "validator";

export const GUEST_CSV_COLUMNS = ["name", "email", "phone"];

export const SAMPLE_CSV_ROWS = [
  ["John Doe", "john@example.com", "+201234567890"],
  ["Jane Smith", "jane@example.com", ""],
];

const COLUMN_ALIASES = {
  name: ["name", "full name", "full_name", "guest name", "guest_name"],
  email: ["email", "e-mail", "e_mail"],
  phone: ["phone", "mobile", "tel", "phone number", "phone_number"],
};

const normalizeHeader = (header) =>
  header
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

const resolveColumnIndex = (headers) => {
  const normalized = headers.map(normalizeHeader);
  const indices = { name: -1, email: -1, phone: -1 };

  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    for (let i = 0; i < normalized.length; i++) {
      const h = normalized[i];
      if (aliases.some((alias) => h === alias || h === alias.replace(/ /g, "_"))) {
        indices[field] = i;
        break;
      }
    }
  }

  return indices;
};

/**
 * Parse a single CSV line respecting double-quoted fields.
 */
const parseCsvLine = (line) => {
  const fields = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  fields.push(current);
  return fields.map((f) => f.trim());
};

const parseCsvRows = (text) => {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  const rows = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    rows.push(parseCsvLine(trimmed));
  }

  return rows;
};

const escapeCsvField = (value) => {
  const str = value == null ? "" : String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const guestsToCsv = (guests) => {
  const lines = [GUEST_CSV_COLUMNS.join(",")];
  for (const guest of guests) {
    lines.push(
      GUEST_CSV_COLUMNS.map((col) => escapeCsvField(guest[col] || "")).join(",")
    );
  }
  return lines.join("\r\n");
};

export const getSampleCsvContent = () => {
  const lines = [GUEST_CSV_COLUMNS.join(",")];
  for (const row of SAMPLE_CSV_ROWS) {
    lines.push(row.map(escapeCsvField).join(","));
  }
  return lines.join("\r\n");
};

export const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * @param {string} text - Raw CSV file contents
 * @returns {{ guests: Array<{name:string,email:string,phone:string}>, errors: string[], skippedCount: number }}
 */
export const parseCsvToGuests = (text) => {
  const errors = [];
  const guests = [];
  let skippedCount = 0;

  const rows = parseCsvRows(text);
  if (rows.length === 0) {
    return { guests, errors: ["CSV file is empty."], skippedCount };
  }

  const headers = rows[0];
  const indices = resolveColumnIndex(headers);

  if (indices.name === -1) {
    return { guests, errors: ['Missing required column: "name" (or alias: full name).'], skippedCount };
  }
  if (indices.email === -1) {
    return { guests, errors: ['Missing required column: "email".'], skippedCount };
  }

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 1;
    const name = (row[indices.name] || "").trim();
    const email = (row[indices.email] || "").trim();
    const phone = indices.phone >= 0 ? (row[indices.phone] || "").trim() : "";

    if (!name && !email) {
      skippedCount++;
      continue;
    }

    if (!name) {
      errors.push(`Row ${rowNum}: missing name.`);
      continue;
    }
    if (!email) {
      errors.push(`Row ${rowNum}: missing email.`);
      continue;
    }
    if (!validator.isEmail(email)) {
      errors.push(`Row ${rowNum}: invalid email "${email}".`);
      continue;
    }

    guests.push({ name, email, phone });
  }

  return { guests, errors, skippedCount };
};

export const hasExportableGuests = (guests) =>
  guests.some((g) => g.name?.trim() && g.email?.trim());
